import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, SmartphoneIcon, TargetIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, SearchIcon, LayoutTemplateIcon, MapPinIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
export function WebDesignSeoPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1013">
      <SEOHead title="Web Design & Local SEO | Phoenix Websites That Convert | Viridia Analytics" description="Fast, mobile-first websites with local SEO that turns Phoenix searches into customers. Speed-optimized, conversion-focused web design with Google Business Profile optimization." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, mobile-first website, conversion-focused design, Google Business Profile optimization, local search optimization, website development Phoenix" data-id="element-1014" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-1015">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1016">
          <div className="absolute top-[10%] left-[10%] w-[40%] h-[40%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-1017"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1018">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1019">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-1020">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-1021">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-1022">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-1023">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-1024">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1025">
                  We build fast, conversion-focused websites with local SEO that
                  makes sure your business shows up when Phoenix customers are
                  searching for what you do.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1026">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-1027">
                    Get a Custom Quote
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1028">
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-1029">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-1030">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-1031">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-1032">
                    <div className="flex space-x-2" data-id="element-1033">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1034"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1035"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1036"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-1037">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-1038" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-1039" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-1040">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1041">
          <ScrollReveal data-id="element-1042">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1043">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1044">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-1045">
                Not just pretty — built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1046">
            <ScrollReveal delay={0.1} data-id="element-1047">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-1048">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-1049">
                  <ZapIcon className="w-7 h-7" data-id="element-1050" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1051">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1052">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1053">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-1054">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-1055">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-1056" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1057">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1058">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-1059">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-1060">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-1061">
                  <TargetIcon className="w-7 h-7" data-id="element-1062" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1063">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1064">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1065">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1066">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1067">
            <div data-id="element-1068">
              <ScrollReveal data-id="element-1069">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1070">
                  Local SEO That Gets You Found
                </h2>
                <p className="text-lg text-gray-400 mb-8" data-id="element-1071">
                  When someone in Phoenix searches for your service, you need to
                  show up. We make that happen.
                </p>
                <div className="space-y-4" data-id="element-1072">
                  {['Google Business Profile Optimization', 'Local Keyword Targeting', 'On-Page SEO & Meta Tags', 'Schema Markup for Local Businesses', 'Citation Building & NAP Consistency', 'Monthly SEO Reporting'].map((feature, index) => <div key={index} className="flex items-center" data-id="element-1073">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0" data-id="element-1074" />
                      <span className="text-gray-300" data-id="element-1075">{feature}</span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-1076">
              <ScrollReveal direction="left" data-id="element-1077">
                {/* Mock Google Search Result */}
                <div className="bg-white rounded-xl p-6 shadow-2xl" data-id="element-1078">
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100" data-id="element-1079">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-1080">
                      <MapPinIcon className="w-6 h-6 text-gray-500" data-id="element-1081" />
                    </div>
                    <div data-id="element-1082">
                      <h4 className="text-lg font-bold text-blue-800 hover:underline cursor-pointer" data-id="element-1083">
                        Phoenix Premier Services
                      </h4>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-1084">
                        <span className="text-yellow-500 flex items-center mr-2" data-id="element-1085">
                          4.9 <StarIcon className="w-3 h-3 ml-1 fill-current" data-id="element-1086" />
                        </span>
                        (128) · Service Business
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-1087">
                    <div className="flex items-center text-sm text-gray-600" data-id="element-1088">
                      <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-1089" />
                      Serving Phoenix, Scottsdale, and surrounding areas
                    </div>
                    <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-1090">
                      <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-1091" />
                      Open 24/7 · Responds in minutes
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2" data-id="element-1092">
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-1093">
                      Website
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-1094">
                      Directions
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-1095">
                      Call
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-1096">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1097">
          <ScrollReveal data-id="element-1098">
            <div className="text-center mb-16" data-id="element-1099">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1100">
                Our Work
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-1101">
                Clean, modern websites that represent your brand and drive
                results.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1102">
            {[{
            name: 'TFP Landscaping',
            type: 'Landscaping & Outdoor Services',
            url: 'https://www.tfplandscaping.com',
            domain: 'tfplandscaping.com',
            image: 'https://cdn.magicpatterns.com/uploads/pNqDjKbbn559dXQNHJrNcf/image.png'
          }, {
            name: 'Finish Coat Paint AZ',
            type: 'Painting & Contracting',
            url: 'https://www.finishcoatpaintaz.com',
            domain: 'finishcoatpaintaz.com',
            image: 'https://cdn.magicpatterns.com/uploads/2MaqmMhJUBbWkMzmHWrQb7/image.png'
          }].map((project, index) => <ScrollReveal key={index} delay={index * 0.2} data-id="element-1103">
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="group block" data-id="element-1104">
                  <div className="bg-gray-900 rounded-t-xl px-4 py-3 flex items-center border-b border-gray-800" data-id="element-1105">
                    <div className="flex space-x-2" data-id="element-1106">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-1107"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-1108"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-1109"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-1110">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-1111" />
                      {project.domain}
                    </div>
                  </div>
                  <div className="border-x border-b border-gray-200 rounded-b-xl h-72 relative overflow-hidden group-hover:shadow-xl transition-all duration-300" data-id="element-1112">
                    <img src={project.image} alt={`${project.name} website screenshot`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" data-id="element-1113" />
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1114">
                      <span className="text-white font-semibold flex items-center" data-id="element-1115">
                        Visit Site <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1116" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1117">
                    <h4 className="text-xl font-bold text-gray-900" data-id="element-1118">
                      {project.name}
                    </h4>
                    <p className="text-gray-500" data-id="element-1119">{project.type}</p>
                  </div>
                </a>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-1120">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-1121"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1122">
          <ScrollReveal data-id="element-1123">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1124">
              Ready for a Website That Actually Works?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-1125">
              Get a custom quote for a site built to convert visitors into
              customers.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-1126">
              Get Your Custom Quote
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1127" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}